import classNames from 'classnames';
import { ReactNode } from 'react';

/**
 * [MCV-1529] `FormSection` normally wraps EVERY child in the SAME
 * `md:col-span-*` derived from its shared `componentsPerLine` prop, so a call
 * site can never give one child a different width than its siblings -- e.g.
 * pairing a full-width field with a narrower adjacent action on the same row
 * is structurally inexpressible (`componentsPerLine` picks one division for
 * the whole section, not per child). `FormCell` is the opt-in escape hatch:
 * `FormSection` recognises a `FormCell` child by element type (see
 * `child.type === FormCell` in FormSection.tsx) and renders it as-is instead
 * of wrapping it a second time, so this cell's own `span` wins independently
 * of `componentsPerLine`. Every other child keeps going through the existing
 * shared-span wrapping, so the 42 existing `FormSection` call sites need zero
 * edits.
 *
 * Tailwind v4 (`@tailwindcss/postcss`) only generates CSS for class names it
 * finds as LITERAL strings while scanning source files -- a template
 * interpolation like `md:col-span-${span}` would silently produce no CSS for
 * any value not ALSO spelled out literally somewhere else in the app. This
 * lookup enumerates the full 1-12 range so every value is guaranteed to
 * compile, the same pattern `layoutByComponentsPerLine` already uses in
 * FormSection.tsx.
 */
export type FormCellSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

const SPAN_CLASSES: Record<FormCellSpan, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
};

export interface FormCellProps {
  /**
   * How many of the enclosing FormSection's 12 grid columns this cell spans.
   * Deliberately a union rather than `number`: only 1-12 have generated CSS
   * (see SPAN_CLASSES above), so an out-of-range or mistyped value used to fall
   * back to full width silently and produce a wrong layout with nothing to
   * diagnose. As a union it is a compile error at the call site instead.
   */
  span: FormCellSpan;
  children: ReactNode;
  className?: string;
}

/**
 * Opt-in per-child span override for `FormSection` -- use it when a call site
 * needs to mix a differently-sized child into an otherwise uniform
 * `componentsPerLine` row. Only meaningful inside a `md:grid-cols-12`
 * FormSection (`componentsPerLine` 1-4); the `componentsPerLine={5}` layout
 * uses a 5-column grid instead, so a FormCell's `span` is not commensurable
 * with it.
 */
export const FormCell = ({ span, children, className }: FormCellProps) => {
  // The union makes this unreachable from typed callers, but JS callers and
  // computed values can still get here — fail loudly in dev rather than
  // silently rendering the wrong width.
  //
  // Validate the VALUE rather than just truth-testing the lookup: a key like
  // 'toString' or 'constructor' resolves an inherited Object.prototype member,
  // which is truthy and would sail past both the warning and the fallback,
  // landing an arbitrary function in the className. The raw value is passed as
  // a separate console argument rather than interpolated, since JSON.stringify
  // throws on circular structures.
  const isValidSpan = Number.isInteger(span) && span >= 1 && span <= 12;
  const spanClass = isValidSpan ? SPAN_CLASSES[span] : undefined;
  if (!isValidSpan && import.meta.env.DEV) {
    console.error('[FormCell] span must be an integer 1-12. Falling back to full width.', span);
  }

  return (
    <div className={classNames('min-w-0', spanClass ?? SPAN_CLASSES[12], className)}>
      {children}
    </div>
  );
};
